import React from 'react';
import {
  CaretDownOutlined,
  CaretUpOutlined,
  FileTextOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Dropdown, Input, Menu } from 'antd';
import OwlLogo from '../../assets/owl-logo-purple.svg';
import { Locale } from '../../context/AppProvider';
import type { RecentItem } from '../recentsStorage';
import styles from './ClaimPicker.module.scss';

interface Props {
  view: 'home' | 'claim';
  activeTitle?: string;
  activeDossierId?: string | null;
  recents: RecentItem[];
  onNavigate: (path: string) => void;
}

const HOME_PATH = '/home';

const ClaimPicker: React.FC<Props> = ({
  view,
  activeTitle,
  activeDossierId,
  recents,
  onNavigate,
}) => {
  const { messages } = React.useContext(Locale);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const inputRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (open) {
      setQuery('');
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const claims = React.useMemo(
    () => recents.filter((r) => r.type === 'dossier' && r.dossierId),
    [recents]
  );

  const q = query.trim().toLowerCase();
  const matches = claims.filter(
    (c) =>
      !q ||
      c.title.toLowerCase().includes(q) ||
      (c.subtitle ?? '').toLowerCase().includes(q)
  );

  const claimItems: MenuProps['items'] = matches.map((c) => ({
    key: `claim:${c.dossierId}`,
    icon: <FileTextOutlined />,
    label: (
      <span>
        {c.title}
        {c.subtitle && <span className={styles.cid}>({c.subtitle})</span>}
      </span>
    ),
  }));

  const homeItem = {
    key: 'home',
    icon: (
      <span className={styles.owlMark}>
        <OwlLogo width={14} height={14} viewBox="0 0 12 12" />
      </span>
    ),
    label: messages['esc.nav.home'] ?? 'Home',
  };

  const items: MenuProps['items'] = q
    ? [
        {
          type: 'group',
          label: messages['esc.tabs.claims'] ?? 'Claims',
          children: claimItems.length
            ? claimItems
            : [{ key: 'none', disabled: true, label: 'No matching claims' }],
        },
      ]
    : [
        homeItem,
        { type: 'divider' },
        {
          type: 'group',
          label: messages['esc.nav.recent'] ?? 'Recent',
          children: claimItems,
        },
      ];

  const onClick: MenuProps['onClick'] = ({ key }) => {
    setOpen(false);
    if (key === 'home') {
      onNavigate(HOME_PATH);
    } else if (key.startsWith('claim:')) {
      onNavigate(`/claims/${key.slice('claim:'.length)}`);
    }
  };

  const panel = (
    <div className={styles.panel}>
      <Input
        ref={inputRef}
        className={styles.search}
        prefix={<SearchOutlined />}
        placeholder={messages['esc.nav.search'] ?? 'Search claims…'}
        allowClear
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
      />
      <Menu
        selectable
        selectedKeys={[
          view === 'home' ? 'home' : `claim:${activeDossierId ?? ''}`,
        ]}
        items={items}
        onClick={onClick}
      />
    </div>
  );

  const isHome = view === 'home';

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      popupRender={() => panel}
    >
      <button type="button" className={styles.pill}>
        {isHome ? (
          <span className={styles.owlMark}>
            <OwlLogo width={14} height={14} viewBox="0 0 12 12" />
          </span>
        ) : (
          <>
            <span className={styles.prefix}>
              {messages['esc.nav.policy'] ?? 'Policy'}
            </span>
            <span className={styles.divider} />
            <FileTextOutlined className={styles.icon} />
          </>
        )}
        <span className={styles.label}>
          {isHome ? messages['esc.nav.home'] ?? 'Home' : activeTitle ?? 'Claim'}
        </span>
        <span className={styles.caret}>
          <CaretUpOutlined />
          <CaretDownOutlined />
        </span>
      </button>
    </Dropdown>
  );
};

export default ClaimPicker;
